import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    IconButton
} from '@mui/material';
import { Close as CloseIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import RootDialogPage from '../../Commun/components/RootDialogPage';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { profilesApi, Profile } from '../services/profilesApi';
import { gammeApi, Gamme } from '../services/gammeApi';
import { MaterialType } from '../services/windowsApi';

interface ProfileEditDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    profile: Profile | null;
}

export const ProfileEditDialog: React.FC<ProfileEditDialogProps> = ({
    open,
    onClose,
    onSuccess,
    profile
}) => {
    const [formData, setFormData] = useState<Partial<Profile>>({
        name: '',
        code: '',
        materialType: MaterialType.Aluminum,
        description: '',
        width: 0,
        height: 0,
        depth: 0,
        unitPrice: 0,
        stockQuantity: 0,
        photo: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [gammes, setGammes] = useState<Gamme[]>([]);
    const { t } = useLanguage();

    useEffect(() => {
        if (open) {
            gammeApi.getAll().then(res => {
                if (res.data) setGammes(res.data);
            }).catch(err => console.error("Error loading gammes", err));
        }
    }, [open]);

    useEffect(() => {
        if (profile) {
            setFormData({ ...profile });
        } else {
            setFormData({
                name: '',
                code: '',
                materialType: MaterialType.Aluminum,
                description: '',
                width: 0,
                height: 0,
                depth: 0,
                unitPrice: 0,
                stockQuantity: 0,
                gammeId: undefined,
                photo: ''
            });
        }
        setError(null);
    }, [profile, open]);

    const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) { // 10MB limit
                setError(t('maker.msg.error.image_size') || 'L\'image doit faire moins de 10MB');
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData({ ...formData, photo: reader.result as string });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemovePhoto = () => {
        setFormData({ ...formData, photo: '' });
    };

    const handleSubmit = async () => {
        if (!formData.name) {
            setError(t('maker.msg.error.required'));
            return;
        }

        try {
            setLoading(true);
            if (profile) {
                await profilesApi.update(profile.id, formData);
            } else {
                await profilesApi.create(formData);
            }
            onSuccess();
            onClose();
        } catch (err: any) {
            console.error("Error saving profile", err);
            setError(err.message || t('maker.msg.failedSave', 'profile'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <RootDialogPage
            open={open}
            onClose={onClose}
            title={profile ? t('maker.profiles.edit') : t('maker.profiles.add')}
            onAction={handleSubmit}
            actionLabel={t('common.actions.save')}
            actionDisabled={loading}
            maxWidth="sm"
            fullWidth
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                <TextField
                    label={t('maker.field.name')}
                    fullWidth
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    error={!!error && !formData.name}
                    helperText={!!error && !formData.name ? t('maker.msg.error.required') : ""}
                />
                <TextField
                    label={t('maker.field.code')}
                    fullWidth
                    value={formData.code || ''}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder="PRF-..."
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <FormControl fullWidth>
                        <InputLabel>{t('maker.field.materialType')}</InputLabel>
                        <Select
                            value={formData.materialType}
                            label={t('maker.field.materialType')}
                            onChange={(e) => setFormData({ ...formData, materialType: Number(e.target.value) })}
                        >
                            <MenuItem value={MaterialType.Aluminum}>{t('maker.materials.type.aluminum')}</MenuItem>
                            <MenuItem value={MaterialType.PVC}>{t('maker.materials.type.pvc')}</MenuItem>
                            <MenuItem value={MaterialType.Wood}>{t('maker.materials.type.wood')}</MenuItem>
                            <MenuItem value={MaterialType.Steel}>{t('maker.materials.type.steel')}</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <InputLabel>{t('maker.gamme.title') || "Gamme"}</InputLabel>
                        <Select
                            value={formData.gammeId || ''}
                            label={t('maker.gamme.title') || "Gamme"}
                            onChange={(e) => setFormData({ ...formData, gammeId: e.target.value ? Number(e.target.value) : undefined })}
                        >
                            <MenuItem value=""><em>{t('common.labels.none') || "None"}</em></MenuItem>
                            {gammes.map(g => (
                                <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <TextField
                    label={t('maker.field.description')}
                    fullWidth
                    multiline
                    rows={2}
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                        label={t('maker.field.width')}
                        type="number"
                        fullWidth
                        value={formData.width}
                        onChange={(e) => setFormData({ ...formData, width: Number(e.target.value) })}
                    />
                    <TextField
                        label={t('maker.field.height')}
                        type="number"
                        fullWidth
                        value={formData.height}
                        onChange={(e) => setFormData({ ...formData, height: Number(e.target.value) })}
                    />
                    <TextField
                        label={t('maker.field.depth')}
                        type="number"
                        fullWidth
                        value={formData.depth}
                        onChange={(e) => setFormData({ ...formData, depth: Number(e.target.value) })}
                    />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                        label={t('maker.field.unitPrice')}
                        type="number"
                        fullWidth
                        value={formData.unitPrice}
                        onChange={(e) => setFormData({ ...formData, unitPrice: Number(e.target.value) })}
                    />
                    <TextField
                        label={t('maker.field.stockQuantity')}
                        type="number"
                        fullWidth
                        value={formData.stockQuantity}
                        onChange={(e) => setFormData({ ...formData, stockQuantity: Number(e.target.value) })}
                    />
                </Box>
                <Box sx={{ mt: 1, mb: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        {t('maker.field.photo') || "Photo"}
                    </Typography>
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                        {formData.photo ? (
                            <Box position="relative">
                                <img
                                    src={formData.photo}
                                    alt="Profile"
                                    style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }}
                                />
                                <IconButton
                                    size="small"
                                    sx={{ position: 'absolute', top: -10, right: -10, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                                    onClick={handleRemovePhoto}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ) : (
                            <Box
                                component="label"
                                sx={{
                                    border: '1px dashed grey',
                                    borderRadius: 1,
                                    p: 3,
                                    width: '100%',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: 'action.hover' }
                                }}
                            >
                                <CloudUploadIcon fontSize="large" color="action" />
                                <Typography variant="body1" color="textSecondary" mt={1}>
                                    {t('maker.action.upload_photo') || "Upload Photo"}
                                </Typography>
                                <input
                                    type="file"
                                    hidden
                                    accept="image/png, image/jpeg, image/gif"
                                    onChange={handleImageUpload}
                                />
                            </Box>
                        )}
                    </Box>
                </Box>
                {error && <Box sx={{ color: 'error.main', mt: 1 }}>{error}</Box>}
            </Box>
        </RootDialogPage>
    );
};
